import { error } from '@chakra-ui/utils'
import UploadCvIcon from '@core/components/ApplyDialogV2/assets/UploadCvIcon'
import type { FileError } from '@core/app/types'
import clsx from 'clsx'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import UnsupportedFileMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/UnsupportedFileMaskIcon'
import DefaultMaskIcon from '@core/components/ApplyDialogV2/FileUploaderV2/DefaultMaskIcon'
import React, { type FC, useRef, useState, useCallback, useEffect, useId } from 'react'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import FileUploaderV2ListItem from '@core/components/ApplyDialogV2/FileUploaderV2ListItem'
import './FileUploaderV2.scss'

const baseClass = 'FileUploaderV2'

type FileWithStatus = {
    file: File
    status: 'uploading' | 'success' | 'error'
    error?: string
    progress?: number
}

interface IFileUploaderV2Props {
    label?: string | null
    id?: string | number | null
    name?: string | null
    isRequired?: boolean | null
    disabled?: boolean
    onClick?: () => void
    onDragOver?: () => void
    onDragLeave?: () => void
    onHandleDrop?: (files: File[]) => void
    onChange?: (files: File[]) => void
    onError?: (errors: FileError[]) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    multiple?: boolean
    multipleLimit?: number
    accept?: string[]
    maxFileSize?: number
    files?: File[]
    children?: React.ReactNode
    className?: string
    defaultMask?: boolean
    description?: React.ReactNode
    errorMessage?: string | null | boolean | React.ReactNode
    isShowErrorMessage?: boolean
    isLoading?: boolean
}

const FileUploaderV2: FC<IFileUploaderV2Props> = ({
    label,
    id,
    name,
    isRequired = false,
    disabled = false,
    onChange = () => {},
    onHandleDrop = () => {},
    onDragOver = () => {},
    onDragLeave = () => {},
    onClick = () => {},
    onError = () => {},
    onFocus = () => {},
    onBlur: handleBlur = () => {},
    multiple = false,
    multipleLimit = 1,
    accept = ['pdf', 'doc', 'docx'],
    maxFileSize = 10 * 1024 * 1024,
    files = [],
    children,
    className = '',
    defaultMask = false,
    description,
    isShowErrorMessage = true,
    errorMessage,
    isLoading = false,
}) => {
    const defaultId = useId()
    const { getTitle } = useApplyV2NavTranslations()
    const inputRef = useRef<HTMLInputElement>(null)
    const [fileItems, setFileItems] = useState<FileWithStatus[]>([])
    const [dragCounter, setDragCounter] = useState(0)
    const [dragging, setDragging] = useState(false)
    const [hovering, setHovering] = useState(false)
    const [isInvalidFiles, setIsInvalidFiles] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const selectId = id ? id.toString() : defaultId

    // useEffect(() => {
    //     const handleDocumentClick = (e: MouseEvent) => {
    //         if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
    //             console.log('Clicked outside component')
    //         }
    //     }
    //
    //     document.addEventListener('click', handleDocumentClick)
    //     return () => document.removeEventListener('click', handleDocumentClick)
    // }, [])

    const acceptString = accept.map((ext) => (ext.includes('/') ? ext : '.' + ext)).join(',')

    const addFiles = (newFiles: File[]) => {
        const newFileItems = newFiles.map((file) => ({
            file,
            status: 'uploading' as const,
            progress: 0,
        }))
        setFileItems((prev) => [...prev, ...newFileItems])
    }

    const validateFilesSize = (files: File[]): boolean => {
        const errors: { file: File; error: string }[] = []

        // for (const file of files) {
        //     if (maxFileSize && file.size > maxFileSize) {
        //         console.log('maxFileSize', file.size)
        //         errors.push({
        //             file,
        //             error: `File "${file.name}" is too large. Max size is ${maxFileSize / 1024 / 1024} MB.`,
        //         })
        //     }
        // }

        if (errors.length > 0) {
            onError(errors)
            return false
        }
        return true
    }

    const validateFilesCount = (files: File[]): File[] => {
        if (!multiple) {
            return files.slice(0, 1)
        }
        if (multipleLimit && files.length > multipleLimit) {
            onError([
                {
                    file: null as any,
                    error: `You can upload max ${multipleLimit} files`,
                },
            ])
            return files.slice(0, multipleLimit)
        }
        return files
    }

    const isFileAccepted = (file: File): boolean => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
        return accept.some((ext) => ext.toLowerCase() === fileExtension || ext.toLowerCase() === `.${fileExtension}`)
    }

    const handleClick = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
            e?.stopPropagation()
            if (!disabled && inputRef.current) {
                // inputRef.current.focus()
                // console.log('Click')
                inputRef.current.click()
                setIsInvalidFiles(false)
            }
            onClick()
        },
        [disabled, onClick]
    )

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            // console.log('FileUploaderV2 handleDragOver called', Array.from(e.dataTransfer.files))
            // console.log(e.dataTransfer.types)
            e.preventDefault()
            // e.stopPropagation()
            if (disabled) return
            // setDragging(true)
        },
        [disabled]
    )

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        // console.log('FileUploaderV2 handleDragEnter called', Array.from(e.dataTransfer.files))
        // e.stopPropagation()
        e.preventDefault()
        if (disabled) return
        setDragCounter((c) => c + 1)
        setDragging(true)
        onDragOver()
        handleDragOver(e)
        setIsInvalidFiles(false)
    }

    // const handleDragEnter = useCallback(
    //     (e: React.DragEvent<HTMLDivElement>) => {
    //         console.log('FileUploaderV2 handleDragEnter called')
    //         e.stopPropagation()
    //         e.preventDefault()
    //         if (disabled) return
    //         setDragCounter((c) => c + 1)
    //         setDragging(true)
    //         console.log('handleDragEnter:', Array.from(e.dataTransfer.files))
    //         onDragOver()
    //         handleDragOver(e)
    //     },
    //     [disabled, onDragOver]
    // )

    const handleDragLeave = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            // console.log('FileUploaderV2 handleDragLeave called', Array.from(e.dataTransfer.files))
            // e.stopPropagation()
            // e.preventDefault()
            if (disabled) return
            setDragCounter((c) => {
                const newCount = c - 1
                if (newCount <= 0) {
                    setDragging(false)
                    onDragLeave()
                    return 0
                }
                return newCount
            })
        },
        [disabled, onDragLeave]
    )

    const removeFile = (fileToRemove: File) => {
        setFileItems((items) => items.filter((item) => item.file !== fileToRemove))
    }

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            console.log('FileUploaderV2 handleDrop called', Array.from(e.dataTransfer.files))
            e.preventDefault()
            e.stopPropagation()
            if (disabled) return
            setDragCounter(0)
            setDragging(false)

            let droppedFiles = Array.from(e.dataTransfer.files)
            const hasInvalidFiles = droppedFiles.some((file) => !isFileAccepted(file))
            if (hasInvalidFiles) {
                setIsInvalidFiles(true)
                console.log('FileUploaderV2 handleDrop: Invalid file types detected', droppedFiles)
                onError([
                    {
                        file: null as any,
                        error: `Only ${accept.join(', ')} files are allowed`,
                    },
                ])
                return
            }
            droppedFiles = validateFilesCount(droppedFiles)

            if (!validateFilesSize(droppedFiles)) {
                return
            }

            if (droppedFiles.length > 0) {
                addFiles(droppedFiles)
                onHandleDrop(droppedFiles)
                onChange(droppedFiles)
            }
        },
        [disabled, multiple, multipleLimit, maxFileSize, onError, onHandleDrop, onChange]
    )
    // const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    //     e.preventDefault()
    //     e.stopPropagation()
    // }, [])

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let files = Array.from(e.target.files || [])

            files = validateFilesCount(files)

            if (!validateFilesSize(files)) {
                e.target.value = ''
                return
            }

            if (files.length > 0) {
                onError([])
                console.log('Files selected:', files)
                onChange(files)
            }

            e.target.value = ''
        },
        [multiple, multipleLimit, maxFileSize, onChange, onError]
    )

    const getStateClass = () => {
        if (disabled) return 'disabled'
        if (dragging) return 'dragging'
        if (hovering) return 'hover'
        return 'default'
    }

    const handleInputBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // console.log('Input blur event:')
            setIsFocused(false)

            // Check if we're blurring outside the component
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                handleBlur(e)
            }
        },
        [handleBlur]
    )

    const handleInputFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // console.log('Input focus event:')
            setIsFocused(true)
            onFocus(e)
        },
        [onFocus]
    )

    const handleWrapperFocus = useCallback(() => {
        // console.log('Wrapper focus event:')
        if (!isFocused && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isFocused])

    const handleWrapperBlur = useCallback(() => {
        // console.log('Wrapper blur event:')
        if (!isFocused && inputRef.current) {
            inputRef.current.blur()
        }
    }, [isFocused])

    // console.log(isFocused)

    return (
        <div
            className={clsx(`${baseClass}__wrapper`, {
                [`${className}__wrapper`]: className,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : undefined}>
                    {label}
                    {isRequired && <span className={`${baseClass}__required`}>*</span>}
                </label>
            )}
            <div
                draggable="false"
                className={clsx(`${baseClass}`, {
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--dragging`]: dragging,
                    [`${baseClass}--hover`]: hovering,
                    [`${baseClass}--unsupported`]: isInvalidFiles,
                    [`${baseClass}--error`]: !!errorMessage && !isFocused,
                    [`${baseClass}--loading`]: isLoading,
                    [`${className}`]: !!className,
                    [`${className}--disabled`]: className && disabled,
                    [`${className}--dragging`]: className && dragging,
                    [`${className}--hover`]: className && hovering,
                    [`${className}--unsupported`]: className && isInvalidFiles,
                    [`${className}--error`]: className && !!errorMessage && !isFocused,
                    [`${className}--loading`]: className && isLoading,
                })}
                // onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                // onFocus={onFocus}
                // onFocus={(e) => {
                //     inputRef.current?.focus()
                // }}
                // onBlur={(e) => {
                //     inputRef.current?.blur()
                //     // onBlur(e)
                // }}
                // onFocus={handleWrapperFocus}
                // onBlur={handleWrapperBlur}
                onMouseEnter={() => !disabled && setHovering(true)}
                onMouseLeave={() => !disabled && setHovering(false)}
                // tabIndex={0}
                role="button"
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (disabled) return
                    if (e.key === 'Enter' || e.key === ' ') {
                        console.log('onKeyDown', e.key)
                        // e.preventDefault()
                        // handleClick()
                    }
                }}
            >
                {isLoading && (
                    <div
                        className={clsx(`${baseClass}__loader`, {
                            [`${className}__loader`]: className && isLoading,
                        })}
                    ></div>
                )}
                <input
                    id={selectId}
                    name={name ?? selectId}
                    type="file"
                    accept={acceptString}
                    ref={inputRef}
                    onChange={(e) => {
                        // console.log('onChange', e)
                        handleChange(e)
                    }}
                    disabled={disabled}
                    className={`${baseClass}__input`}
                    multiple={multiple}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                />
                <div
                    className={`${baseClass}__content`}
                    onClick={(e) => {
                        handleClick()
                    }}
                >
                    {isInvalidFiles ? (
                        <>
                            <div className={`${baseClass}__icon`}>
                                <UnsupportedFileMaskIcon />
                            </div>
                            <p>{getTitle('6200', 'File format is not supported')}</p>
                        </>
                    ) : children ? (
                        children
                    ) : defaultMask ? (
                        <>
                            <div className={`${baseClass}__icon`}>
                                <DefaultMaskIcon />
                            </div>
                            <p>Drop files here or click to upload</p>
                        </>
                    ) : (
                        <>
                            <UploadCvIcon className={`${baseClass}__icon`} />
                            <p>{dragging ? 'Release file to upload' : 'Drop CV here or click to upload'}</p>
                            <span>{accept.join(' / ').toUpperCase()}</span>
                        </>
                    )}
                </div>
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4800_38853)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4800_38853">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default FileUploaderV2
